import { Grid, useTheme } from '@mui/material';
import React, { Suspense, useState } from 'react';
import { FullScreenHandle, useFullScreenHandle } from 'react-full-screen';
import styled from 'styled-components';
import {
  useDisplayGlobalContext,
  useGlobalFilterContext,
  useGlobalSegmentationLevel1Context,
  useGlobalTimeSliderContext,
} from '../../../context/contexts';
import { GlobalDisplayState } from '../../../context/types';
import { FullHeightFullScreen } from '../../styles';
import { ManualDataRefetchCountHandle } from '../../types';
import { ChartGridConfig, DashboardPageOptions } from '../dashboards/types';
import { Filter, Segment } from '../filter/filterbar/types';
import { ItemsState } from '../filter/types';
import { TimeSliderState } from '../timeslider/types';
import { ChartErrorBoundary } from './ChartErrorBoundary';
import { Skeleton } from './Skeleton';

interface ChartWrapperProps {
  children: (props: {
    showTools: boolean;
    fullScreenHandle: FullScreenHandle;
    timeSliderState: TimeSliderState | null;
    filtersState: ItemsState<Filter>;
    segmentationState: ItemsState<Segment>;
    displayGlobalState: GlobalDisplayState;
  }) => React.ReactNode;
  manualDataRefetchCountHandle: ManualDataRefetchCountHandle;
  config: ChartGridConfig;
  pageOptions: DashboardPageOptions;
  hasTools?: boolean;
}

const StyledChartWrapper = styled('div')`
  position: relative;
  padding: 0.25rem;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

export const ChartWrapper = (props: ChartWrapperProps) => {
  const {
    children,
    manualDataRefetchCountHandle,
    config,
    pageOptions,
    hasTools = true,
  } = props;
  const { filterTray, timeSlider, segmentationLevel1 } = pageOptions;
  const fullScreenHandle = useFullScreenHandle();
  const timeSliderHandle = useGlobalTimeSliderContext();
  const timeSliderEnabled = timeSlider?.enabled ?? true;
  const filterTrayEnabled = filterTray?.enabled ?? true;
  const segmentationLevel1Enabled = segmentationLevel1?.enabled ?? true;
  const [timeSliderState] = timeSliderHandle;
  // const finalTimeSliderState = timeSliderEnabled ? timeSliderState : null;
  const finalTimeSliderState = null;
  // We need to discuss how the timeSlider is supposed to work with
  // the chart components. The current approach of just overwriting the time
  // selection to be based on the external time slider does not
  // map well to the real client use cases we have.
  // What we can do now is this - when time slider is enabled
  // we use the single point time selector and use that to get relative
  // time queries for all the charts
  // In the future, we can discuss how the time slider fits into the storyboard
  const globalFilterHandle = useGlobalFilterContext();
  const [filtersState] = globalFilterHandle;
  const finalFiltersState = filterTrayEnabled
    ? filtersState
    : { ...filtersState, items: [] };
  const globalSegmentationHandle = useGlobalSegmentationLevel1Context();
  const [segmentationState] = globalSegmentationHandle;
  const finalSegmentationState = segmentationLevel1Enabled
    ? segmentationState
    : { ...segmentationState, items: [] };
  const displayGlobalHandle = useDisplayGlobalContext();
  const [displayGlobalState] = displayGlobalHandle;
  const [showTools, setShowTools] = useState(false);
  const handleShowTools = (value: boolean) => {
    hasTools && setShowTools(value);
  };
  const theme = useTheme();
  // if start or end present, don't use span
  const gridColumn =
    config.gridColStart || config.gridColEnd
      ? undefined
      : `span ${config.gridColSpan ?? 1}`;
  const gridRow =
    config.gridRowStart || config.gridRowEnd
      ? undefined
      : `span ${config.gridRowSpan ?? 1}`;

  return (
    <Grid
      item
      sx={{
        display: 'grid',
        position: 'relative', // Equivalent to relative
        gridColumnStart: config.gridColStart ?? 'auto',
        gridColumnEnd: config.gridColEnd ?? 'auto',
        gridRowStart: config.gridRowStart ?? 'auto',
        gridRowEnd: config.gridRowEnd ?? 'auto',
        gridColumn,
        gridRow,
        height: '100%',
        [theme.breakpoints.down('lg')]: {
          height: '40vh',
          gridColumn: 'span 1',
          gridRow: 'span 1',
          gridColumnStart: 'auto',
          gridColumnEnd: 'auto',
          gridRowStart: 'auto',
          gridRowEnd: 'auto',
        },
      }}
    >
      <FullHeightFullScreen
        handle={fullScreenHandle}
        $fullScreenActive={fullScreenHandle.active}
      >
        <ChartErrorBoundary
          manualDataRefetchCountHandle={manualDataRefetchCountHandle}
        >
          <Suspense fallback={<Skeleton />}>
            <StyledChartWrapper
              onMouseOver={() => handleShowTools(true)}
              onMouseOut={() => handleShowTools(false)}
            >
              {children({
                showTools,
                fullScreenHandle,
                timeSliderState: finalTimeSliderState,
                filtersState: finalFiltersState,
                segmentationState: finalSegmentationState,
                displayGlobalState,
              })}
            </StyledChartWrapper>
          </Suspense>
        </ChartErrorBoundary>
      </FullHeightFullScreen>
    </Grid>
  );
};
